import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Chess } from 'chess.js';
import Square from './Square';
import { useGameStore } from '@/store/gameStore';
import { FILES, RANKS } from '@/engine/constants';
import { getBestMoveStockfish, getCoachSuggestion, analyzePosition } from '@/engine/stockfishAI';
import { playMoveSound, playCaptureSound } from '@/utils/sounds';

// Helper to find all squares attacking a given square
function getAttackers(game, targetSquare, attackerColor) {
  const attackers = [];
  const board = game.board();
  
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      const piece = board[r][f];
      if (piece && piece.color === attackerColor) {
        const fromSquare = String.fromCharCode(97 + f) + (8 - r);
        const moves = game.moves({ square: fromSquare, verbose: true });
        if (moves.some(m => m.to === targetSquare)) {
          attackers.push({ square: fromSquare, piece });
        }
      }
    }
  }
  return attackers;
}

// Find all pieces of a color that are under attack
function findPiecesInDanger(game, playerColor) {
  const opponentColor = playerColor === 'white' ? 'b' : 'w';
  const myColor = playerColor === 'white' ? 'w' : 'b';
  const board = game.board();
  const dangeredSquares = [];
  
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      const piece = board[r][f];
      if (piece && piece.color === myColor) {
        const square = String.fromCharCode(97 + f) + (8 - r);
        const attackers = getAttackers(game, square, opponentColor);
        
        if (attackers.length > 0) {
          // Check if the piece is defended
          const defenders = getAttackers(game, square, myColor);
          const isUndefended = defenders.length === 0;
          
          // Calculate material value
          const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 };
          const pieceValue = pieceValues[piece.type] || 0;
          
          // Check if attacker is of lower value (losing exchange)
          const attackerValues = attackers.map(a => pieceValues[a.piece.type] || 0);
          const lowestAttacker = Math.min(...attackerValues);
          const wouldLoseMaterial = lowestAttacker < pieceValue;
          
          dangeredSquares.push({
            square,
            piece,
            isUndefended,
            wouldLoseMaterial,
            attackerCount: attackers.length,
            severity: isUndefended ? 'high' : (wouldLoseMaterial ? 'medium' : 'low')
          });
        }
      }
    }
  }
  
  return dangeredSquares;
}

const ChessBoard = () => {
  const {
    game,
    position,
    playerColor,
    selectedSquare,
    legalMoves,
    lastMove,
    showLegalMoves,
    showCoordinates,
    boardTheme,
    soundEnabled,
    coachEnabled,
    isAIThinking,
    suggestedMove,
    gameStatus,
    selectSquare,
    makeMove,
    makeAIMove,
    setAIThinking,
    setEvaluation,
    setSuggestedMove,
    aiDifficulty,
    arrowCount,
    showDangerHighlights
  } = useGameStore();

  const aiMoveInProgress = useRef(false);

  // Parse position to board array
  const boardArray = useMemo(() => {
    const tempGame = new Chess(position);
    return tempGame.board();
  }, [position]);

  // Handle square click
  const handleSquareClick = useCallback((square) => {
    if (gameStatus !== 'playing' || isAIThinking) return;
    selectSquare(square);
  }, [selectSquare, gameStatus, isAIThinking]);

  // AI move effect - using Stockfish
  useEffect(() => {
    if (gameStatus !== 'playing') return;
    if (aiMoveInProgress.current) return;
    
    const isAITurn = (game.turn() === 'w' && playerColor === 'black') ||
                     (game.turn() === 'b' && playerColor === 'white');
    
    if (isAITurn && !isAIThinking) {
      aiMoveInProgress.current = true;
      setAIThinking(true);
      
      // Use async Stockfish for AI move
      const makeStockfishMove = async () => {
        try {
          const gameCopy = new Chess(position);
          const move = await getBestMoveStockfish(gameCopy, aiDifficulty);
          
          if (move) {
            const result = makeAIMove(move);
            
            if (result && soundEnabled) {
              if (result.captured) {
                playCaptureSound();
              } else {
                playMoveSound();
              }
            }
          }
        } catch (error) {
          console.error('AI move error:', error);
        } finally {
          setAIThinking(false);
          aiMoveInProgress.current = false;
        }
      };
      
      // Small delay for UI feedback
      setTimeout(makeStockfishMove, 300);
    }
  }, [position, playerColor, isAIThinking, gameStatus, aiDifficulty]);

  // Coach analysis after AI move - get suggestion for player using Stockfish
  useEffect(() => {
    if (!coachEnabled || gameStatus !== 'playing') return;
    
    const isPlayerTurn = (game.turn() === 'w' && playerColor === 'white') ||
                         (game.turn() === 'b' && playerColor === 'black');
    
    if (isPlayerTurn && !isAIThinking) {
      // Clear old suggestion immediately while calculating new one
      setSuggestedMove(null);
      
      // Get suggestion with Stockfish analysis
      const getStockfishSuggestion = async () => {
        try {
          const gameCopy = new Chess(position);
          
          // Get position evaluation
          const analysis = await analyzePosition(position);
          if (analysis) {
            setEvaluation(analysis.evalPercent - 50); // Convert to -50 to +50 scale
          }
          
          // Get coach suggestion with predicted continuation
          const suggestion = await getCoachSuggestion(gameCopy);
          if (suggestion && suggestion.from && suggestion.to) {
            setSuggestedMove(suggestion);
          }
        } catch (e) {
          console.error('Coach analysis error:', e);
        }
      };
      
      // Small delay before analysis
      const timer = setTimeout(getStockfishSuggestion, 200);
      
      return () => clearTimeout(timer);
    } else {
      // Not player's turn - ensure no arrow shows
      setSuggestedMove(null);
    }
  }, [position, coachEnabled, playerColor, gameStatus, isAIThinking]);

  // Check if current player's king is in check
  const kingInCheckSquare = useMemo(() => {
    const tempGame = new Chess(position);
    if (!tempGame.isCheck()) return null;
    
    // Find the king that's in check
    const turn = tempGame.turn();
    const board = tempGame.board();
    
    for (let r = 0; r < 8; r++) {
      for (let f = 0; f < 8; f++) {
        const piece = board[r][f];
        if (piece && piece.type === 'k' && piece.color === turn) {
          const file = String.fromCharCode(97 + f);
          const rank = 8 - r;
          return file + rank;
        }
      }
    }
    return null;
  }, [position]);

  // Find pieces in danger (for coach mode)
  const dangerSquares = useMemo(() => {
    if (!coachEnabled || !showDangerHighlights || gameStatus !== 'playing' || isAIThinking) {
      return [];
    }
    
    const tempGame = new Chess(position);
    const isPlayerTurn = (tempGame.turn() === 'w' && playerColor === 'white') ||
                         (tempGame.turn() === 'b' && playerColor === 'black');
    
    // Only show danger when it's player's turn
    if (!isPlayerTurn) return [];
    
    return findPiecesInDanger(tempGame, playerColor);
  }, [position, coachEnabled, showDangerHighlights, gameStatus, isAIThinking, playerColor]);

  // Generate squares
  const squares = useMemo(() => {
    const result = [];
    const ranks = playerColor === 'white' ? RANKS : [...RANKS].reverse();
    const files = playerColor === 'white' ? FILES : [...FILES].reverse();
    
    for (let rankIdx = 0; rankIdx < 8; rankIdx++) {
      for (let fileIdx = 0; fileIdx < 8; fileIdx++) {
        const file = files[fileIdx];
        const rank = ranks[rankIdx];
        const square = file + rank;
        
        // Get piece at square
        const boardRank = 8 - parseInt(rank);
        const boardFile = file.charCodeAt(0) - 97;
        const piece = boardArray[boardRank]?.[boardFile];
        
        const isLight = (rankIdx + fileIdx) % 2 === 0;
        const isSelected = selectedSquare === square;
        const isLegalMoveTarget = showLegalMoves && legalMoves.includes(square);
        const isLastMoveSquare = lastMove && (lastMove.from === square || lastMove.to === square);
        const isSuggestedSquare = suggestedMove && suggestedMove.to === square;
        const isKingInCheck = kingInCheckSquare === square;
        
        // Check if square has a piece in danger
        const dangerInfo = dangerSquares.find(d => d.square === square);
        
        result.push({
          square,
          piece,
          isLight,
          isSelected,
          isLegalMove: isLegalMoveTarget,
          isLastMove: isLastMoveSquare,
          isSuggested: coachEnabled && isSuggestedSquare,
          isInCheck: isKingInCheck,
          isInDanger: coachEnabled && !!dangerInfo,
          dangerSeverity: dangerInfo?.severity || null,
          file: fileIdx,
          rank: parseInt(rank)
        });
      }
    }
    
    return result;
  }, [boardArray, playerColor, selectedSquare, legalMoves, lastMove, showLegalMoves, suggestedMove, coachEnabled, kingInCheckSquare, dangerSquares]);

  return (
    <div 
      className={`
        relative aspect-square w-full max-w-[560px]
        rounded-xl overflow-hidden
        ${boardTheme === 'cosmic' 
          ? 'shadow-glow-cyan border border-cyan-500/20' 
          : 'shadow-2xl border-8 border-amber-900 ring-4 ring-amber-950/50'
        }
      `}
      style={boardTheme === 'classic' ? {
        backgroundImage: 'linear-gradient(135deg, #92400e 0%, #78350f 50%, #92400e 100%)'
      } : {}}
      data-testid="chess-board"
    >
      {/* Glow effect for cosmic theme */}
      {boardTheme === 'cosmic' && (
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/5 via-transparent to-emerald-600/5 pointer-events-none z-10" />
      )}
      
      {/* Wood grain effect for classic theme */}
      {boardTheme === 'classic' && (
        <div className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(0,0,0,0.1) 2px,
              rgba(0,0,0,0.1) 4px
            )`
          }}
        />
      )}
      
      {/* Board grid */}
      <div className="grid grid-cols-8 grid-rows-8 w-full h-full">
        {squares.map(({ square, piece, isLight, isSelected, isLegalMove, isLastMove, isSuggested, isInCheck, isInDanger, dangerSeverity, file, rank }) => (
          <Square
            key={square}
            square={square}
            piece={piece}
            isLight={isLight}
            isSelected={isSelected}
            isLegalMove={isLegalMove}
            isLastMove={isLastMove}
            isSuggested={isSuggested}
            isInCheck={isInCheck}
            isInDanger={isInDanger}
            dangerSeverity={dangerSeverity}
            showCoordinates={showCoordinates}
            file={file}
            rank={rank}
            theme={boardTheme}
            onClick={() => handleSquareClick(square)}
          />
        ))}
      </div>
      
      {/* AI thinking overlay */}
      {isAIThinking && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/30 flex items-center justify-center z-20"
        >
          <div className="bg-slate-900/90 px-6 py-3 rounded-full flex items-center gap-3 border border-cyan-500/30">
            <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-cyan-400 font-medium">AI Thinking...</span>
          </div>
        </motion.div>
      )}
      
      {/* Coach suggestion arrows - Full PV line visualization */}
      {coachEnabled && suggestedMove && suggestedMove.from && suggestedMove.to && !isAIThinking && (
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none z-20" 
          viewBox="0 0 8 8"
          key={`arrow-${suggestedMove.from}-${suggestedMove.to}-${arrowCount}`}
        >
          <defs>
            {/* Primary arrow - Your best move (bright green) */}
            <marker
              id="arrowhead-primary"
              markerWidth="3"
              markerHeight="3"
              refX="1.5"
              refY="1.5"
              orient="auto"
            >
              <polygon points="0,0 3,1.5 0,3" fill="#00FF88" />
            </marker>
            {/* Opponent response arrow (orange/amber) */}
            <marker
              id="arrowhead-opponent"
              markerWidth="2.5"
              markerHeight="2.5"
              refX="1.25"
              refY="1.25"
              orient="auto"
            >
              <polygon points="0,0 2.5,1.25 0,2.5" fill="#FF6B35" />
            </marker>
            {/* Your follow-up arrows (cyan) */}
            <marker
              id="arrowhead-followup"
              markerWidth="2.5"
              markerHeight="2.5"
              refX="1.25"
              refY="1.25"
              orient="auto"
            >
              <polygon points="0,0 2.5,1.25 0,2.5" fill="#00D4FF" />
            </marker>
            {/* Later moves (faded) */}
            <marker
              id="arrowhead-later"
              markerWidth="2"
              markerHeight="2"
              refX="1"
              refY="1"
              orient="auto"
            >
              <polygon points="0,0 2,1 0,2" fill="rgba(180,180,180,0.7)" />
            </marker>
            <filter id="arrow-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.08" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="arrow-glow-strong" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.12" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Render PV line arrows based on arrowCount setting */}
          {(() => {
            const arrows = [];
            let pvLine = suggestedMove.pvLine || [];
            
            // If no PV line but we have a suggested move, create a minimal PV line
            if (pvLine.length === 0 && suggestedMove.from && suggestedMove.to) {
              pvLine = [{
                from: suggestedMove.from,
                to: suggestedMove.to,
                san: suggestedMove.san,
                piece: suggestedMove.piece
              }];
              
              // Add predicted response if available
              if (suggestedMove.predictedResponse) {
                pvLine.push(suggestedMove.predictedResponse);
              }
            }
            
            const movesToShow = Math.min(arrowCount || 3, pvLine.length);
            
            // Helper to convert square to board coordinates
            const squareToCoords = (sq) => {
              const fileNum = sq.charCodeAt(0) - 97;
              const rankNum = 8 - parseInt(sq[1]);
              const x = playerColor === 'black' ? 7 - fileNum : fileNum;
              const y = playerColor === 'black' ? 7 - rankNum : rankNum;
              return { x, y };
            };
            
            // Draw each arrow in the PV line
            for (let i = 0; i < movesToShow; i++) {
              const move = pvLine[i];
              if (!move || !move.from || !move.to) continue;
              
              const from = squareToCoords(move.from);
              const to = squareToCoords(move.to);
              
              // Calculate shortened endpoint
              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const len = Math.sqrt(dx * dx + dy * dy);
              const shortenBy = 0.25;
              const endX = to.x - (dx / len) * shortenBy;
              const endY = to.y - (dy / len) * shortenBy;
              
              // Determine arrow style based on move index
              let stroke, strokeWidth, markerEnd, opacity, dashArray;
              
              if (i === 0) {
                // Primary move (your best move) - bright green, thick
                stroke = "#00FF88";
                strokeWidth = 0.22;
                markerEnd = "url(#arrowhead-primary)";
                opacity = 1;
                dashArray = null;
              } else if (i % 2 === 1) {
                // Opponent's response - orange, dashed
                stroke = "#FF6B35";
                strokeWidth = 0.16;
                markerEnd = "url(#arrowhead-opponent)";
                opacity = 0.85 - (i * 0.1);
                dashArray = "0.15 0.08";
              } else {
                // Your follow-up - cyan
                stroke = "#00D4FF";
                strokeWidth = 0.14;
                markerEnd = "url(#arrowhead-followup)";
                opacity = 0.8 - (i * 0.1);
                dashArray = null;
              }
              
              // Later moves get fainter
              if (i >= 3) {
                stroke = "rgba(180,180,180,0.7)";
                markerEnd = "url(#arrowhead-later)";
                strokeWidth = 0.12;
                opacity = 0.5;
              }
              
              arrows.push(
                <motion.line
                  key={`pv-arrow-${i}-${move.from}-${move.to}`}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity }}
                  transition={{ duration: 0.3, delay: i * 0.15 }}
                  x1={from.x + 0.5}
                  y1={from.y + 0.5}
                  x2={endX + 0.5}
                  y2={endY + 0.5}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={dashArray}
                  markerEnd={markerEnd}
                  filter={i === 0 ? "url(#arrow-glow-strong)" : "url(#arrow-glow)"}
                />
              );
            }
            
            return arrows;
          })()}
        </svg>
      )}
    </div>
  );
};

export default ChessBoard;
